import type {
  BrandColour,
  BrandPreset,
  BrandProfile,
  BrandSurface,
  DnsRecord,
  LogoAsset,
} from '@/types/portal';
import { PLATFORM } from '@/config/platform';

/**
 * The agency's public identity. This is what clients and creators see on every
 * branded surface — the portal, emails and invoices.
 */
export const brandProfile: BrandProfile = {
  name: 'Twiva Media Group',
  legalName: 'Twiva Media Group Limited',
  tagline: 'Creator marketing that moves markets.',
  supportEmail: 'hello@kilicreatives.co.ke',
  website: 'https://kilicreatives.co.ke',
  baseCurrency: 'KES',
  markets: ['Kenya', 'Uganda', 'Tanzania', 'DR Congo', 'Ethiopia'],
  logoInitials: 'TM',
  domain: 'portal.kilicreatives.co.ke',
  domainStatus: 'Verified',
};

export const brandCurrencies = ['KES', 'TZS', 'UGX', 'USD'];

/** The three brand roles and what each one is used for. */
export const brandColours: BrandColour[] = [
  {
    role: 'primary',
    label: 'Primary',
    hex: '#8A0E49',
    usage: 'Primary buttons, active navigation and key calls to action',
  },
  {
    role: 'accent',
    label: 'Accent',
    hex: '#12B76A',
    usage: 'Approvals, success states and positive performance deltas',
  },
  {
    role: 'secondary',
    label: 'Secondary',
    hex: '#C21B54',
    usage: 'Supporting controls, chips and secondary panels',
  },
];

/**
 * Ready-made palettes an admin can apply in one click. "Twiva" is the platform default, the Twiva
 * mobile app's brand (burgundy, success green, pink); "Twiva Ember" is the earlier ember palette.
 */
export const brandPresets: BrandPreset[] = [
  { id: 'twiva', name: PLATFORM.name, primary: '#8A0E49', accent: '#12B76A', secondary: '#C21B54', tag: 'Main' },
  { id: 'twiva-ember', name: `${PLATFORM.name} Ember`, primary: '#FF6200', accent: '#089A71', secondary: '#D9A441', tag: 'Secondary' },
  { id: 'terracotta', name: 'Terracotta & Sage', primary: '#B3542E', accent: '#2F7D5B', secondary: '#C9962F' },
  { id: 'clay', name: 'Warm Clay', primary: '#A8452C', accent: '#3E7C74', secondary: '#D9A441' },
  { id: 'espresso', name: 'Espresso', primary: '#6E3B2A', accent: '#4C7A55', secondary: '#B98A3C' },
  { id: 'saffron', name: 'Saffron Field', primary: '#C2571F', accent: '#5C7A3B', secondary: '#E0A72E' },
  { id: 'cocoa', name: 'Warm Cocoa', primary: '#8A4B2A', accent: '#57743C', secondary: '#D2A24C' },
];

/** Logo lockups the portal expects to have on hand. */
export const logoAssets: LogoAsset[] = [
  {
    id: 'mark',
    name: 'Logo mark',
    description: 'Square icon for the app, favicon and social profiles',
    kind: 'PNG · SVG',
    dimensions: '512 × 512',
    transparent: false,
  },
  {
    id: 'wordmark',
    name: 'Wordmark',
    description: 'Horizontal lockup for portal headers and email signatures',
    kind: 'PNG · SVG',
    dimensions: '1200 × 320',
    transparent: true,
  },
  {
    id: 'avatar',
    name: 'Portal avatar',
    description: 'Rounded mark shown in the sidebar and topbar',
    kind: 'PNG',
    dimensions: '256 × 256',
    transparent: false,
  },
];

/** DNS records needed to point a custom domain at the portal. */
export const dnsRecords: DnsRecord[] = [
  { id: 'cname', type: 'CNAME', name: 'portal', value: 'cname.twiva.io', ttl: 'Auto' },
  { id: 'verify', type: 'TXT', name: '_twiva-verify.portal', value: 'twiva-verify=8f2c41a9d7e3', ttl: 'Auto' },
  { id: 'dmarc', type: 'TXT', name: '_dmarc.portal', value: 'v=DMARC1; p=none; rua=mailto:dmarc@kilicreatives.co.ke', ttl: 'Auto' },
];

/** The surfaces your branding is applied to. */
export const brandSurfaces: BrandSurface[] = [
  {
    id: 'client',
    label: 'Client portal',
    description: 'The read-only campaign view your brands sign in to',
    icon: 'ri-briefcase-line',
    enabled: true,
  },
  {
    id: 'creator',
    label: 'Creator portal',
    description: 'Invites, briefs and payout details creators see',
    icon: 'ri-user-star-line',
    enabled: true,
  },
  {
    id: 'email',
    label: 'Email templates',
    description: 'Invitations, approvals and payment notices',
    icon: 'ri-mail-line',
    enabled: true,
  },
  {
    id: 'invoice',
    label: 'Invoices & statements',
    description: 'Headers, footers and PDF exports',
    icon: 'ri-file-text-line',
    enabled: false,
  },
];